/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import sortby from 'lodash.sortby';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getServiceProfiles} from '@ion/core/src/services/Connect';
import {ServiceProfile} from '@ion/core/src/types/entities';
import Dropdown, {createOptions, Options} from '@ion/dls/src/components/menus/Dropdown';

/**
 * Defines the prop required by this component.
 */
export type ServiceProfileChooserProps = {
  onChange?: (serviceProfile: ServiceProfile) => void;
  readOnly?: boolean;
  required?: boolean;
  includeNone?: boolean
  validationError?: string;
  classes?: string;
  defaultValue?: ServiceProfile;
}

const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  }
}));

/**
 * The ServiceProfileChooser provides a list of service profiles for display within a dropdown list
 * allowing the user to select a service profile from the list.
 *
 * ```typescript
 *
 *     import {ServiceProfileChooser} from '@ion/features/src/entities/serviceProfile/ServiceProfileChooser';
 *
 *     <ServiceProfileChooser defaultValue={subscription}
 *        validationError=""
 *        onChange={selectedProfileCallback} />
 *
 * ```
 *
 * @onChange         Callback prop which will be invoked with the seleted Service Profile.
 * @readOnly         When true, the component will be displayed in read-only mode;
 *                   that is, disabled. Defaults to false.
 * @required         When true, the required indicator will be displayed.
 * @validationError  When true, the validationError indicator will be displayed.
 * @classes          Optional classes to apply to the wrapping element.
 * @includeNone      When true (default), localized 'None' option will be displayed.
 * @defaultValue     Default Value of the field
 */
export const ServiceProfileChooser = ({
  onChange,
  readOnly,
  required,
  classes,
  defaultValue,
  validationError,
  includeNone = true
}: ServiceProfileChooserProps) => {
  const _classes                              = useStyles();
  const [serviceProfiles, setServiceProfiles] = useState<ServiceProfile[]>(null);
  const [isLoading, setIsLoading]             = useState<boolean>(false);
  const [options, setOptions]                 = useState<Options[]>([]);
  const [selected, setSelected]               = useState<string>(null);
  const hasMounted                            = useHasMounted();

  useEffect(() => {
    const loadServiceprofiles = async() => {
      setIsLoading(true);
      if (hasMounted !== false) {
        try {
          const results = await getServiceProfiles();
          if (results?.length) {
            const serviceProfilesList = sortby(results, 'name') as ServiceProfile[];
            setServiceProfiles(serviceProfilesList);
            setOptions(createOptions({
              items     : serviceProfilesList,
              valProp   : 'name',
              labelProp : 'name'
            }));
          }
        } catch (err) {
          error('ServiceProfileChooser: Error occurred retrieving service profiles');
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadServiceprofiles();
  }, []);

  useEffect(() => {
    if (defaultValue && !selected) {
      setSelected(defaultValue.name);
    }
  }, [defaultValue]);

  const getSelectedValue = (
    serviceProfiles: ServiceProfile[],
    selected: number | string
  ): ServiceProfile => {
    return serviceProfiles.find(({name}: ServiceProfile) => name === selected);
  };

  return (
    <div className={classes || _classes.root}>
      <Dropdown displaySelectedLabel
        includeNone={includeNone}
        required={required}
        disabled={readOnly || isLoading || options?.length === 0}
        hasError={Boolean(validationError)}
        value={!isLoading ? selected : translate('common:states.loading')}
        options={options}
        fieldLabel={translate('common:editors.serviceProfile.label')}
        placeholder={translate('common:editors.serviceProfile.placeholder')}
        preferPlaceholder={!isLoading && !selected}
        onChange={onChange ? function({target}) {
          const name = target.value as string;
          setSelected(name);
          onChange(getSelectedValue(serviceProfiles, name));
        } : null} />
    </div>
  );
};

export default ServiceProfileChooser;
